import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { hubClient } from '../../services/hubClient';
import { errorText } from '../../lib/assistantView';
import type { BotRef, BotSystem } from '../../lib/botNav';
import { fmtTime } from '../../lib/jobView';
import { JobPanel } from '../knowledge/JobPanel';
import {
  activeRunId,
  estimateLines,
  expectedOptions,
  flowV2Notice,
  fmtUsd,
  rateLabel,
  freshnessNotice,
  runLink,
  scoreText,
  type Estimate,
  type Freshness,
  type LinkOption,
  type QuestionPage,
  type Run,
  type TesterQuestion,
} from '../../lib/testerView';

const PAGE = 100;
const PER_CHUNK = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const BTN = 'px-3 py-1.5 text-sm rounded disabled:opacity-50';

/**
 * Quality › Tests (spec chatbot_v2_mono 2026-10-12-tester-suggestions-design.md §4.4): the bot's test questions (make,
 * edit, skip in runs, delete), starting a run after seeing its size and cost, and the run history. A run is an
 * llm-tester job — it keeps going when this page closes; its own page shows the progress or the result.
 */
export function TestsPanel({ system, botId }: { system: BotSystem; botId: string }) {
  const bot = useMemo<BotRef>(() => ({ system, botId }), [system, botId]);
  const navigate = useNavigate();
  const [fresh, setFresh] = useState<Freshness | null>(null);
  const [page, setPage] = useState<QuestionPage | null>(null);
  const [offset, setOffset] = useState(0);
  const [runs, setRuns] = useState<Run[] | null>(null);
  const [perChunk, setPerChunk] = useState(3);
  const [genJob, setGenJob] = useState<string | null>(null);
  const [estimate, setEstimate] = useState<Estimate | null>(null);
  const [editing, setEditing] = useState<{ id: number; text: string } | null>(null);
  const [links, setLinks] = useState<LinkOption[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadFresh = useCallback(async () => {
    try {
      setFresh(await hubClient.testerFreshness(bot));
    } catch (e) {
      setError(errorText(e));
    }
  }, [bot]);
  const loadQuestions = useCallback(async () => {
    try {
      setPage(await hubClient.testerQuestions(bot, { limit: PAGE, offset }));
    } catch (e) {
      setPage({ items: [], total: 0 });
      setError(errorText(e));
    }
  }, [bot, offset]);
  const loadRuns = useCallback(async () => {
    try {
      setRuns((await hubClient.testerRuns(bot)).items);
    } catch (e) {
      setRuns([]);
      setError(errorText(e));
    }
  }, [bot]);

  useEffect(() => {
    void loadFresh();
    void loadRuns();
  }, [loadFresh, loadRuns]);
  useEffect(() => {
    hubClient
      .testerBlockLinks(bot)
      .then(setLinks)
      .catch(() => setLinks([])); // the picker then offers only (not set) / No block
  }, [bot]);
  useEffect(() => {
    void loadQuestions();
  }, [loadQuestions]);

  async function act(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  const generate = () =>
    act(async () => {
      const r = await hubClient.testerGenerate(bot, perChunk);
      setGenJob(r.jobId);
    });
  const saveEdit = () =>
    act(async () => {
      if (!editing) return;
      await hubClient.testerUpdateQuestion(bot, editing.id, { question: editing.text });
      setEditing(null);
      await loadQuestions();
    });
  const toggle = (q: TesterQuestion) =>
    act(async () => {
      await hubClient.testerUpdateQuestion(bot, q.id, { status: q.status === 'active' ? 'stale' : 'active' });
      await loadQuestions();
    });
  const setExpected = (q: TesterQuestion, value: string) =>
    act(async () => {
      await hubClient.testerUpdateQuestion(bot, q.id, { expectedBlock: value === '' ? null : value });
      await loadQuestions();
    });
  const remove = (q: TesterQuestion) => {
    if (!window.confirm('Delete this question?')) return;
    void act(async () => {
      await hubClient.testerDeleteQuestion(bot, q.id);
      await loadQuestions();
    });
  };
  const removeAll = () => {
    if (!window.confirm(`Delete all ${page?.total ?? 0} questions of this bot? Finished runs keep their results.`)) return;
    void act(async () => {
      await hubClient.testerDeleteQuestions(bot);
      setOffset(0);
      await loadQuestions();
    });
  };
  const prepareRun = () =>
    act(async () => {
      setEstimate(await hubClient.testerEstimate(bot));
    });
  const startRun = () =>
    act(async () => {
      try {
        const r = await hubClient.testerStartRun(bot);
        navigate(runLink(bot, r.jobId));
      } catch (e) {
        const active = activeRunId(e);
        if (!active) throw e;
        navigate(runLink(bot, active)); // one run per bot: open the one already going
      }
    });

  const notice = freshnessNotice(fresh);
  const flow = estimate ? flowV2Notice(estimate.flowVersion) : null;
  return (
    <div className="space-y-6">
      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2">{error}</div>}
      {notice && (
        <div
          className={`text-sm rounded px-3 py-2 border ${notice.tone === 'warn' ? 'text-amber-800 bg-amber-50 border-amber-100' : 'text-blue-800 bg-blue-50 border-blue-100'}`}
        >
          {notice.text}
        </div>
      )}

      <section className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-base font-semibold mr-auto">Run a test</h2>
          <button type="button" disabled={busy} onClick={() => void prepareRun()} className={`${BTN} bg-blue-600 text-white`}>
            Run test…
          </button>
        </div>
        <p className="text-sm text-gray-600">
          Every active question is asked to the bot’s real answer path, each in its own conversation (nothing is sent to customers), and scored right or
          wrong.
        </p>
        {estimate && (
          <div className="bg-white border border-gray-200 rounded-lg p-3 text-sm space-y-2">
            {estimate.questions === 0 ? (
              <p>No active questions — make questions first.</p>
            ) : (
              <ul className="list-disc pl-5">
                {estimateLines(estimate).map((l) => (
                  <li key={l}>{l}</li>
                ))}
              </ul>
            )}
            {flow && <p className="text-amber-800">{flow}</p>}
            <div className="flex gap-2">
              <button type="button" disabled={busy || estimate.questions === 0} onClick={() => void startRun()} className={`${BTN} bg-blue-600 text-white`}>
                Start
              </button>
              <button type="button" onClick={() => setEstimate(null)} className={`${BTN} border border-gray-300`}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-semibold">Runs</h2>
        {runs === null ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : runs.length === 0 ? (
          <p className="text-sm text-gray-500">No runs yet.</p>
        ) : (
          <table className="w-full text-sm bg-white border border-gray-200 rounded-lg">
            <thead className="text-left text-xs text-gray-500">
              <tr>
                <th className="p-2">Started</th>
                <th className="p-2">By</th>
                <th className="p-2">Status</th>
                <th className="p-2">Questions</th>
                <th className="p-2">Score</th>
                <th className="p-2">Right block</th>
                <th className="p-2">Claude cost</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.jobId} className="border-t hover:bg-gray-50 cursor-pointer" onClick={() => navigate(runLink(bot, r.jobId))}>
                  <td className="p-2">{fmtTime(r.createdAt)}</td>
                  <td className="p-2">{r.requestedBy ?? '—'}</td>
                  <td className="p-2">{r.status}</td>
                  <td className="p-2">{r.summary?.total ?? '—'}</td>
                  <td className="p-2">{scoreText(r.summary)}</td>
                  <td className="p-2">{rateLabel(r.summary)}</td>
                  <td className="p-2">{fmtUsd(r.usage?.costUsd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-base font-semibold mr-auto">Questions {page ? `(${page.total})` : ''}</h2>
          <label className="text-sm">
            Per chunk{' '}
            <select value={perChunk} onChange={(e) => setPerChunk(Number(e.target.value))} className="border rounded px-1 py-0.5">
              {PER_CHUNK.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <button type="button" disabled={busy} onClick={() => void generate()} className={`${BTN} border border-gray-300`}>
            Make questions
          </button>
          <button type="button" disabled={busy || !page?.total} onClick={removeAll} className={`${BTN} text-red-600`}>
            Delete all
          </button>
        </div>
        {genJob && (
          <div>
            <JobPanel key={genJob} system={system} botId={botId} jobId={genJob} jobStatus="pending" />
            <button
              type="button"
              className="text-sm text-blue-600 hover:underline"
              onClick={() => {
                void loadQuestions();
                void loadFresh();
              }}
            >
              Refresh questions
            </button>
          </div>
        )}
        {system === 'chatbot_v2' && (
          <p className="text-xs text-gray-500">The Tester asks on Instagram, so the expected block is chosen from this bot’s Instagram block links.</p>
        )}
        {page === null ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : page.items.length === 0 ? (
          <p className="text-sm text-gray-500">No questions.</p>
        ) : (
          <ul className="divide-y bg-white border border-gray-200 rounded-lg">
            {page.items.map((q) => (
              <li key={q.id} className="p-2 text-sm flex items-start gap-3">
                {editing?.id === q.id ? (
                  <>
                    <textarea
                      className="flex-1 border rounded px-2 py-1"
                      rows={2}
                      value={editing.text}
                      onChange={(e) => setEditing({ id: q.id, text: e.target.value })}
                    />
                    <button type="button" disabled={busy} className="text-blue-600 hover:underline" onClick={() => void saveEdit()}>
                      Save
                    </button>
                    <button type="button" className="text-gray-600 hover:underline" onClick={() => setEditing(null)}>
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <span className={`flex-1 ${q.status === 'stale' ? 'text-gray-400 line-through' : ''}`}>{q.question}</span>
                    <label className="text-xs text-gray-500">
                      Expected block{' '}
                      <select
                        aria-label="Expected block"
                        disabled={busy}
                        value={q.expectedBlock ?? ''}
                        onChange={(e) => void setExpected(q, e.target.value)}
                        className="border rounded px-1 py-0.5 text-sm text-gray-900 max-w-48"
                      >
                        <option value="">(not set)</option>
                        <option value="none">No block</option>
                        {expectedOptions(links, system, q.expectedBlock).map((o) => (
                          <option key={o.ref} value={o.ref}>
                            {o.title}
                          </option>
                        ))}
                      </select>
                    </label>
                    <button type="button" className="text-blue-600 hover:underline" onClick={() => setEditing({ id: q.id, text: q.question })}>
                      Edit
                    </button>
                    <button type="button" disabled={busy} className="text-gray-600 hover:underline" onClick={() => void toggle(q)}>
                      {q.status === 'active' ? 'Skip in runs' : 'Use in runs'}
                    </button>
                    <button type="button" disabled={busy} className="text-red-600 hover:underline" onClick={() => remove(q)}>
                      Delete
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
        {page && page.total > PAGE && (
          <div className="flex items-center gap-3 text-sm">
            <button type="button" disabled={offset === 0} className="text-blue-600 disabled:text-gray-400" onClick={() => setOffset(Math.max(0, offset - PAGE))}>
              ← Previous
            </button>
            <span className="text-gray-500">
              {offset + 1}–{Math.min(offset + PAGE, page.total)} of {page.total}
            </span>
            <button
              type="button"
              disabled={offset + PAGE >= page.total}
              className="text-blue-600 disabled:text-gray-400"
              onClick={() => setOffset(offset + PAGE)}
            >
              Next →
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
